import PropTypes from 'prop-types';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import { getTestProps } from '../../../lib/helpers';

import Button from '../../../components/Button/Button';
import LinkButton from '../../../components/LinkButton/LinkButton';

import { useModals } from '../../../contexts/ModalContext';

import { ArrowRightThinIcon } from '../../../images/shapes';

import SpaceLimits from '../../SpacesManage/SpaceLimits/SpaceLimits';

const SeeMoreSpaceUsageButton = ({
  spaceData,
  isAdmin = false,
  testId = '',
}) => {
  const modal = useModals();
  const { t } = useTranslation();
  const [user] = useLocalStorageState('cms.user');

  const openUsageModal = useCallback(() => {
    modal({
      id: 'space-usage-modal',
      title: spaceData?.name,
      size: 'xl',
      contentAdditionalClasses: 'pt-5 pb-10 md:pb-5',
      content: (
        <>
          <div className="fixed w-full z-10 py-2 pl-9 top-0 left-0 bg-slate-50 text-bold capitalize dark:bg-gray-800">
            <h2 className="text-3xl">{spaceData?.name}</h2>
          </div>
          <span className="mt-5 mb-4 font-bold block">
            {t('Spaces.SpaceDetails')}
          </span>
          <SpaceLimits
            id={spaceData?.id}
            slug={spaceData?.slug}
            planLimits={spaceData?.planLimits}
            paymentDueDate={spaceData?.paymentDueDate}
            paymentStatus={spaceData?.paymentStatus}
            isBuyExtraVisible={false}
            showPlanName={false}
          />
          <p className="mt-8">
            {t('Spaces.OrganizationUserUpdateSpaceMessage')}{' '}
            <span className="mb-2 font-bold break-words whitespace-pre-line">
              {user?.data?.organization_admins?.join(', ')}
            </span>
          </p>
        </>
      ),
    });
  }, [modal, spaceData, t, user]);

  const componentProps = useMemo(
    () => (isAdmin ? { link: '/organization' } : { onClick: openUsageModal }),
    [isAdmin, openUsageModal],
  );

  const Component = isAdmin ? LinkButton : Button;

  return (
    <Component
      buttonColor="borderless"
      noPaddings
      buttonSize="sm"
      iconImage={<ArrowRightThinIcon />}
      iconPosition="end"
      iconColor={'blue'}
      {...componentProps}
      additionalClasses="ml-0 mt-7 w-fit"
      {...getTestProps(testId, 'see-more', 'testId')}
    >
      {t('Global.SeeMore')}
    </Component>
  );
};

SeeMoreSpaceUsageButton.propTypes = {
  /**
   * Space data object
   */
  spaceData: PropTypes.object,
  /**
   * Is headless admin
   */
  isAdmin: PropTypes.bool,
  /**
   * Component test id
   */
  testId: PropTypes.string,
};

export default SeeMoreSpaceUsageButton;
